import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  MobileStepper,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FeedbackIcon from '@mui/icons-material/Feedback';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import Link from 'next/link';

export const TUTORIAL_STORAGE_KEY = 'ouaaa_filter_tutorial_seen';

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    maxWidth: 650,
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #2c367e 0%, #69b6a9 100%)',
    padding: '24px 32px',
    color: 'white',
    textAlign: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.3rem',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: '0.9rem',
    opacity: 0.9,
  },
  content: {
    padding: '24px 32px !important',
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: '16px !important',
      minHeight: 'auto',
    },
  },
  stepIcon: {
    fontSize: 48,
    color: '#2c367e',
    marginBottom: 12,
  },
  stepTitle: {
    fontWeight: 700,
    color: '#2c367e',
    fontSize: '1.1rem',
    marginBottom: 12,
  },
  stepDescription: {
    color: '#444',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginBottom: 16,
  },
  mockUI: {
    backgroundColor: '#f5f7ff',
    borderRadius: 12,
    padding: 16,
    border: '2px dashed #2c367e33',
    marginTop: 8,
  },
  mockLabel: {
    fontSize: '0.75rem',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
    fontWeight: 600,
  },
  mockInput: {
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    fontSize: '0.9rem',
    color: '#333',
  },
  mockSlider: {
    height: 6,
    borderRadius: 3,
    background: 'linear-gradient(to right, #69b6a9 50%, #e0e0e0 50%)',
    marginTop: 4,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      top: -5,
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: '#2c367e',
      transform: 'translateX(-50%)',
    },
  },
  mockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#2c367e',
    color: 'white',
    borderRadius: 8,
    padding: '8px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'default',
    boxShadow: '0 2px 8px rgba(44,54,126,0.3)',
  },
  mockNotifRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': { borderBottom: 'none' },
  },
  mockCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 3,
    border: '2px solid #69b6a9',
    backgroundColor: '#69b6a9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  pulseArrow: {
    animation: '$pulse 1.5s ease-in-out infinite',
    color: '#d96552',
    fontSize: '1.2rem',
    marginLeft: 4,
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1, transform: 'translateX(0)' },
    '50%': { opacity: 0.5, transform: 'translateX(4px)' },
  },
  channelGrid: {
    display: 'flex',
    gap: 16,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  channelCard: {
    flex: '1 1 140px',
    border: '2px solid #69b6a9',
    borderRadius: 12,
    padding: 12,
    textAlign: 'center',
    backgroundColor: '#f0faf8',
  },
  channelIcon: {
    fontSize: 32,
    color: '#2c367e',
    marginBottom: 4,
  },
  channelLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#2c367e',
  },
  channelDesc: {
    fontSize: '0.75rem',
    color: '#666',
    marginTop: 2,
  },
  accountPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'linear-gradient(135deg, #f5f7ff, #e8f5e9)',
    borderRadius: 12,
    padding: 16,
    marginTop: 12,
  },
  accountIcon: {
    fontSize: 48,
    color: '#2c367e',
  },
  feedbackBox: {
    textAlign: 'center',
    padding: 16,
    marginTop: 8,
  },
  actions: {
    padding: '12px 24px 20px',
    justifyContent: 'space-between',
  },
  stepper: {
    padding: '0 16px 16px',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  mobileStepper: {
    background: 'transparent',
    [theme.breakpoints.up('sm')]: { display: 'none' },
  },
  skipButton: {
    color: '#999',
    textTransform: 'none',
    fontSize: '0.85rem',
  },
}));

const STEPS = [
  { label: 'Lieu & distance' },
  { label: 'Catégories' },
  { label: 'Sauvegarder' },
  { label: 'Notifications' },
  { label: 'Mon compte' },
  { label: 'Votre avis' },
];

interface FilterTutorialProps {
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

function FilterTutorial({ open: controlledOpen, onClose: controlledOnClose, onOpen }: FilterTutorialProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  useEffect(() => {
    if (isControlled) return;
    if (typeof window === 'undefined') return;
    const seen = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (!seen) {
      setInternalOpen(true);
    }
  }, [isControlled]);

  useEffect(() => {
    if (isOpen && onOpen) onOpen();
  }, [isOpen, onOpen]);

  useEffect(() => {
    if (isControlled) return;
    if (typeof window === 'undefined') return;
    const openHandler = () => {
      setActiveStep(0);
      setInternalOpen(true);
    };
    window.addEventListener('ouaaa:open-filter-tutorial', openHandler);
    return () => window.removeEventListener('ouaaa:open-filter-tutorial', openHandler);
  }, [isControlled]);

  const handleClose = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    }
    setActiveStep(0);
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, controlledOnClose]);

  const handleNext = useCallback(() => {
    if (activeStep < STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      handleClose();
    }
  }, [activeStep, handleClose]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('ouaaa:tutorial-step', { detail: { step: activeStep } }));
    } else {
      window.dispatchEvent(new CustomEvent('ouaaa:tutorial-step', { detail: { step: -1 } }));
    }
  }, [activeStep, isOpen]);

  const renderStep = () => {
    switch (activeStep) {
      case 0: return renderStepLocation();
      case 1: return renderStepCategories();
      case 2: return renderStepSave();
      case 3: return renderStepNotifications();
      case 4: return renderStepAccount();
      case 5: return renderStepFeedback();
      default: return null;
    }
  };

  const renderStepLocation = () => (
    <>
      <Box textAlign="center"><LocationOnIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">🗺️ Filtrer par lieu et distance</Typography>
      <Typography className={classes.stepDescription}>
        Vous pouvez désormais rechercher les acteurs et événements autour d'un lieu précis !
        Saisissez une adresse ou une ville, puis ajustez le rayon de recherche avec le curseur.
      </Typography>
      <Box className={classes.mockUI}>
        <Typography className={classes.mockLabel}>Exemple :</Typography>
        <div className={classes.mockInput}>
          <LocationOnIcon style={{ color: '#69b6a9', fontSize: 20 }} />
          <span>La Rochelle</span>
          <span className={classes.pulseArrow}>← Cliquez ici</span>
        </div>
        <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
          Distance : <strong>50 km</strong>
        </Typography>
        <div className={classes.mockSlider} />
      </Box>
    </>
  );

  const renderStepCategories = () => (
    <>
      <Box textAlign="center"><TuneIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">🏷️ Sélectionner des catégories</Typography>
      <Typography className={classes.stepDescription}>
        Affinez votre recherche en sélectionnant les catégories qui vous intéressent.
      </Typography>
      <Box className={classes.mockUI}>
        <Typography className={classes.mockLabel}>Exemple : sélection de catégories</Typography>
        <Box textAlign="center" my={1}>
          <img
            src="/tutorial/tutorial_filtre_amap.png"
            alt="Exemple de sélection de catégories avec AMAP"
            style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e0e0e0' }}
          />
        </Box>
      </Box>
    </>
  );

  const renderStepSave = () => (
    <>
      <Box textAlign="center"><SaveIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">💾 Sauvegarder votre filtre</Typography>
      <Typography className={classes.stepDescription}>
        Une fois vos filtres configurés, cliquez sur le bouton « Sauvegarder ce filtre » en bas du panneau.
      </Typography>
      <Box className={classes.mockUI}>
        <Box textAlign="center" mb={2}>
          <div className={classes.mockButton}>
            <SaveIcon style={{ fontSize: 18 }} />
            Sauvegarder ce filtre
          </div>
        </Box>
        <div className={classes.mockInput} style={{ border: '2px solid #2c367e' }}>
          <span style={{ color: '#888' }}>Nom du filtre :</span>
          <span style={{ fontWeight: 600 }}>AMAP La Rochelle 50km</span>
        </div>
      </Box>
    </>
  );

  const renderStepNotifications = () => (
    <>
      <Box textAlign="center"><NotificationsActiveIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">🔔 Activer les notifications</Typography>
      <Typography className={classes.stepDescription}>
        Après avoir sauvegardé, cliquez sur l'icône 🔔 à côté de votre filtre pour configurer vos notifications.
      </Typography>
      <Box className={classes.mockUI}>
        <Typography className={classes.mockLabel}>Être notifié pour :</Typography>
        {['acteurs', 'événements', 'recettes', 'articles'].map((item) => (
          <div key={item} className={classes.mockNotifRow}>
            <div className={classes.mockCheckbox}>✓</div>
            <span>Nouveaux <strong>{item}</strong></span>
          </div>
        ))}
        <Typography className={classes.mockLabel} style={{ marginTop: 16 }}>Canal de notification :</Typography>
        <Box className={classes.channelGrid}>
          <div className={classes.channelCard}>
            <EmailIcon className={classes.channelIcon} />
            <div className={classes.channelLabel}>Email</div>
            <div className={classes.channelDesc}>Dès qu'un contenu apparaît ou regroupé par fréquence</div>
          </div>
          <div className={classes.channelCard}>
            <NotificationsActiveIcon className={classes.channelIcon} />
            <div className={classes.channelLabel}>Push notification</div>
            <div className={classes.channelDesc}>Notifications dans votre navigateur ou sur Android</div>
            <Box display="flex" alignItems="center" justifyContent="center" gap="4px" mt={1}>
              <LanguageIcon style={{ fontSize: 16, color: '#69b6a9' }} />
              <Typography variant="caption" style={{ color: '#69b6a9', fontWeight: 500 }}>Navigateur</Typography>
              <span style={{ color: '#ccc', margin: '0 4px' }}>|</span>
              <PhoneAndroidIcon style={{ fontSize: 16, color: '#69b6a9' }} />
              <Typography variant="caption" style={{ color: '#69b6a9', fontWeight: 500 }}>App Android</Typography>
            </Box>
          </div>
        </Box>
      </Box>
    </>
  );

  const renderStepAccount = () => (
    <>
      <Box textAlign="center"><AccountCircleIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">👤 Retrouvez vos filtres dans Mon Compte</Typography>
      <Typography className={classes.stepDescription}>
        Tous vos filtres sauvegardés sont accessibles depuis <strong>Mon Compte → Filtres sauvegardés</strong>.
      </Typography>
      <Box className={classes.accountPreview}>
        <AccountCircleIcon className={classes.accountIcon} />
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#2c367e' }}>Mon Compte</Typography>
          <Typography variant="body2" color="textSecondary">→ Mes informations</Typography>
          <Typography variant="body2" style={{ fontWeight: 600, color: '#69b6a9' }}>
            → Filtres sauvegardés
          </Typography>
          <Typography variant="body2" color="textSecondary">→ Sécurité</Typography>
        </Box>
      </Box>
    </>
  );

  const renderStepFeedback = () => (
    <>
      <Box textAlign="center"><FeedbackIcon className={classes.stepIcon} /></Box>
      <Typography className={classes.stepTitle} align="center">💬 Avez-vous trouvé ce tutoriel intéressant ?</Typography>
      <Typography className={classes.stepDescription} style={{ textAlign: 'center' }}>
        Votre avis nous aide à améliorer la plateforme OUAAA!
      </Typography>
      <Box className={classes.feedbackBox}>
        <Link href="/contact" passHref legacyBehavior>
          <Button
            variant="contained"
            size="large"
            startIcon={<FeedbackIcon />}
            onClick={handleClose}
            style={{
              backgroundColor: '#d96552',
              color: 'white',
              borderRadius: 30,
              padding: '12px 32px',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Ouvrir le formulaire de contact
          </Button>
        </Link>
        <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 16 }}>
          Merci de contribuer à la transition écologique et sociale ! 🌱
        </Typography>
      </Box>
    </>
  );

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      hideBackdrop={isMobile}
      disableEnforceFocus={isMobile}
      disableScrollLock={isMobile}
      sx={isMobile ? {
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          pointerEvents: 'none',
        },
        '& .MuiDialog-paper': {
          marginTop: 1,
          marginX: 1,
          marginBottom: 0,
          width: 'calc(100% - 16px)',
          maxHeight: '55vh',
          pointerEvents: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        },
      } : undefined}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.header}>
        <Typography className={classes.headerTitle}>🌿 Découvrez les nouvelles fonctionnalités</Typography>
        <Typography className={classes.headerSubtitle}>Filtres par lieu, sauvegarde et notifications</Typography>
      </Box>

      {!isMobile && (
        <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
          {STEPS.map((step) => (
            <Step key={step.label}><StepLabel>{step.label}</StepLabel></Step>
          ))}
        </Stepper>
      )}

      {isMobile && (
        <MobileStepper
          variant="dots"
          steps={STEPS.length}
          position="static"
          activeStep={activeStep}
          className={classes.mobileStepper}
          backButton={<span />}
          nextButton={<span />}
        />
      )}

      <DialogContent className={classes.content}>{renderStep()}</DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} className={classes.skipButton}>
          {activeStep === STEPS.length - 1 ? 'Fermer' : 'Passer le tutoriel'}
        </Button>
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} startIcon={<KeyboardArrowLeft />}>
              Précédent
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={activeStep < STEPS.length - 1 ? <KeyboardArrowRight /> : null}
            style={{
              backgroundColor: activeStep === STEPS.length - 1 ? '#69b6a9' : '#2c367e',
              color: 'white',
              marginLeft: 8,
              borderRadius: 8,
            }}
          >
            {activeStep === STEPS.length - 1 ? 'Terminer' : 'Suivant'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default FilterTutorial;
